import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'

// import { getQueryParamsObjectToString } from 'utils/helpers/router'
import { toastSuccess } from 'utils/toast'
import { getAllCategories, getAlllocation } from 'utils/api'
import { callErrorMsg } from 'helpers/errorMsg'
import { getElligibleRacks } from 'features/Rack/services/rackList'
import {
  updatePutAway,
  getDirectPutaway,
  getStaffByPhone,
  completePutAway,
  resetPutaway,
  type GetDirectPutawayResponseType,
  type GetDirectPutawayRequestType,
  type GetStaffResponseType,
  type UpdatePutAwayPayloadType,
} from 'utils/apiList/direcPutaway'
import {
  validatePurchaseOrderComplete,
  type ValidatePurchaseOrderCompletePayloadInterface,
} from 'features/PurchaseOrder/services/purchaseOrder'

export interface DirectPutawayInitialStateInterface {
  isLoading: boolean
  isLoadingUpdateTask: boolean
  isLoadingSearchRacks: boolean
  putawayList: GetDirectPutawayResponseType['data']
  locationList: {
    location_id: number
    location_name: string
    location_type: string
  }[]
  categories: {
    category_hidden: boolean
    category_id: number
    category_name: string
  }[]
  selectedCategory: DirectPutawayInitialStateInterface['categories'][0] | ObjectNullType
  selectedLocation: DirectPutawayInitialStateInterface['locationList'][0] | ObjectNullType
  popUpState: {
    type: string
    isShow: boolean
    putaway?: DirectPutawayInitialStateInterface['putawayList'][0] | ObjectNullType
  }
  rackList: {
    id: number
    rackName: string
  }[]
  staff: GetStaffResponseType['data'] | ObjectNullType
  query: GetDirectPutawayRequestType['params']
  pagination: GetDirectPutawayResponseType['pagination']
}

export enum PutawayStatusEnum {
  COMPLETED = 'COMPLETED',
  NEW = 'NEW',
  IN_PROGRESS = 'IN_PROGRESS',
}

export enum PutawayActionEnum {
  PROCESS = 'Proses',
  COMPLETE = 'Selesaikan',
  RESET = 'Reset Task',
}

export enum PutawayModalTypeEnum {
  SHOP_KEEPER = 'SHOP_KEEPER',
  CONFIRMATION = 'CONFIRMATION',
  RESET = 'RESET',
}

const initialState: DirectPutawayInitialStateInterface = {
  isLoading: false,
  isLoadingUpdateTask: false,
  isLoadingSearchRacks: false,
  locationList: [],
  categories: [],
  selectedCategory: {},
  selectedLocation: {},
  putawayList: [],
  popUpState: {
    type: '',
    isShow: false,
    putaway: {},
  },
  rackList: [
    {
      id: 0,
      rackName: '',
    },
  ],
  query: {
    page_index: 1,
    page_size: 10,
    sort: 'created_at',
    location_ids: '',
    sku_number: '',
    package_label: '',
    product_category_id: 0,
    status: '',
    inbound_date: '',
  },
  staff: {
    staffId: 0,
    staffFullName: '',
    staffPhoneNumber: '',
  },
  pagination: {
    direction: 'desc',
    number_of_elements: 0,
    page_index: 1,
    page_size: 10,
    sort: 'created_at',
  },
}

export const sliceName = 'masterApproval'

const appSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    reset: () => initialState,
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setLoadingUpdateTask: (state, action: PayloadAction<boolean>) => {
      state.isLoadingUpdateTask = action.payload
    },
    setLoadingSearchRacks: (state, action: PayloadAction<boolean>) => {
      state.isLoadingSearchRacks = action.payload
    },
    setLocationList: (
      state,
      action: PayloadAction<DirectPutawayInitialStateInterface['locationList']>,
    ) => {
      state.locationList = action.payload
    },
    setCategories: (
      state,
      action: PayloadAction<DirectPutawayInitialStateInterface['categories']>,
    ) => {
      state.categories = action.payload
    },
    setPopUpState: (
      state,
      action: PayloadAction<DirectPutawayInitialStateInterface['popUpState']>,
    ) => {
      state.popUpState = action.payload
    },
    setQuery: (state, action: PayloadAction<DirectPutawayInitialStateInterface['query']>) => {
      state.query = action.payload
    },
    setPutawayList: (
      state,
      action: PayloadAction<DirectPutawayInitialStateInterface['putawayList']>,
    ) => {
      state.putawayList = action.payload
    },
    setRackList: (state, action: PayloadAction<DirectPutawayInitialStateInterface['rackList']>) => {
      state.rackList = action.payload
    },
    setStaff: (state, action: PayloadAction<GetStaffResponseType['data']>) => {
      state.staff = action.payload
    },
    setPagination: (state, action: PayloadAction<GetDirectPutawayResponseType['pagination']>) => {
      state.pagination = action.payload
    },
    setClosePopup: (state) => {
      state.staff = {}
      state.popUpState = {
        isShow: false,
        type: '',
        putaway: {},
      }
    },
  },
})

export const {
  setLoading,
  setLoadingUpdateTask,
  setLocationList,
  setCategories,
  setPopUpState,
  setQuery,
  setPutawayList,
  setRackList,
  setLoadingSearchRacks,
  setStaff,
  setPagination,
  setClosePopup,
} = appSlice.actions

export default appSlice.reducer

export const updatePutAwayTask = createAsyncThunk(
  `${sliceName}/updatePutAwayTask`,
  async (
    {
      id,
      rackName,
      payload,
    }: {
      id: number
      rackName?: string
      payload: UpdatePutAwayPayloadType
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoadingUpdateTask(true))
      await updatePutAway(id, payload)
      await dispatch(getPutAwayTaskList())

      if (rackName) {
        toastSuccess('Update Sloc Berhasil')
      } else {
        toastSuccess('Assign Staff Berhasil')
      }
      dispatch(setClosePopup())
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingUpdateTask(false))
    }
  },
)

export const completePutAwayTask = createAsyncThunk(
  `${sliceName}/completePutAway`,
  async (
    {
      id,
      packageId,
      payload,
    }: { id: number; packageId: string; payload: UpdatePutAwayPayloadType },
    { dispatch },
  ) => {
    try {
      dispatch(setLoadingUpdateTask(true))
      await completePutAway(id, payload)
      await dispatch(getPutAwayTaskList())
      dispatch(setClosePopup())
      toastSuccess(`Berhasil Proses Putaway ${packageId}`)
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingUpdateTask(false))
    }
  },
)

export const resetPutawayTask = createAsyncThunk(
  `${sliceName}/resetPutawayTask`,
  async (
    {
      id,
      packageId,
    }: {
      id: number
      packageId: string
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoadingUpdateTask(true))
      await resetPutaway(id)
      await dispatch(getPutAwayTaskList())
      dispatch(setClosePopup())
      toastSuccess(`Reset putaway ${packageId} berhasil`)
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingUpdateTask(false))
    }
  },
)

const getQueryStringOfLocationId = (stringIds: string, fieldName: string) =>
  stringIds
    .split(',')
    .map((id) => `${fieldName}=${encodeURIComponent(id)}`)
    .join('&')

const getQueryParamsForSubmit = (params: GetDirectPutawayRequestType['params']) => {
  const queryParamData: Partial<GetDirectPutawayRequestType['params']> = { page_size: 10 }
  if (params.page_index) queryParamData.page_index = params.page_index
  if (params.sort) queryParamData.sort = params.sort
  if (params.status) queryParamData.status = params.status
  if (params.sku_number) queryParamData.sku_number = params.sku_number
  if (params.package_label) queryParamData.package_label = params.package_label
  if (params.inbound_date) queryParamData.inbound_date = params.inbound_date

  const stringifiedQueryParamData = Object.fromEntries(
    Object.entries(queryParamData).map(([key, value]) => [key, value?.toString() || '']),
  )
  let queryString = new URLSearchParams(stringifiedQueryParamData).toString()
  if (params.location_ids) {
    const listLocationIdQueryString = getQueryStringOfLocationId(
      params.location_ids,
      'location_ids',
    )
    queryString = `${queryString}&${listLocationIdQueryString}`
  }
  return queryString
}

export const getPutAwayTaskList = createAsyncThunk(
  `${sliceName}/getPutAwayTaskList`,
  async (_, { dispatch, getState }) => {
    try {
      const {
        directPutaway: { query },
      } = getState() as StoreStateType
      dispatch(setLoading(true))

      const queryString = getQueryParamsForSubmit(query)
      const { data } = await getDirectPutaway(queryString)
      dispatch(setPutawayList(data.data))
      dispatch(setPagination(data.pagination))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

type GetSlocListParamsType = {
  location_id: number
  product_id: number
  search: string
  is_empty_dpf: boolean
}

export const getSlocList = createAsyncThunk(
  `${sliceName}/getSlocList`,
  async (
    { location_id, product_id, search, is_empty_dpf }: GetSlocListParamsType,
    { dispatch },
  ) => {
    try {
      dispatch(setLoadingSearchRacks(true))

      const { data } = await getElligibleRacks({
        page_index: 1,
        page_size: 30,
        status_id: 1,
        location_id,
        product_id,
        search,
        is_empty_dpf,
      })

      let rackList: {
        id: number
        rackName: string
      }[] = []

      if (data?.data?.length) {
        rackList = data?.data
          ?.filter((rackItem) => rackItem.active)
          .map((rackItem) => ({
            id: rackItem.id,
            rackName: rackItem.rack_name,
          }))
      }

      dispatch(setRackList(rackList))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingSearchRacks(false))
    }
  },
)

export const getStaff = createAsyncThunk(
  `${sliceName}/getStaff`,
  async (_, { dispatch, getState }) => {
    try {
      dispatch(setLoadingSearchRacks(true))
      const {
        auth: { userData },
      } = getState() as StoreStateType
      const { data } = await getStaffByPhone(userData.phone_number)
      if (data?.data) {
        dispatch(
          setStaff({
            staffId: data.data.staff.staff_id,
            staffFullName: data.data.staff.staff_full_name,
            staffPhoneNumber: data.data.staff.staff_phone_number,
          }),
        )

        return data.data.staff.staff_id
      }
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingSearchRacks(false))
    }

    return ''
  },
)

export const getLocations = createAsyncThunk(
  `${sliceName}/getLocations`,
  async (_, { dispatch }) => {
    try {
      const { data } = await getAlllocation()
      if (data?.length) {
        dispatch(setLocationList([...data]))
      }
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const getCategories = createAsyncThunk(
  `${sliceName}/getCategories`,
  async (_, { dispatch }) => {
    try {
      const { data } = await getAllCategories()
      if (data?.length) {
        const filterAvailableCategories = data?.filter(
          (x: DirectPutawayInitialStateInterface['categories'][0]) => !x.category_hidden,
        )
        dispatch(setCategories([...filterAvailableCategories]))
      }
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const actValidatePoComplete =
  (payload: ValidatePurchaseOrderCompletePayloadInterface, callbackFunction: () => void) =>
  async () => {
    try {
      await validatePurchaseOrderComplete(payload)
      callbackFunction()
    } catch (error) {
      callErrorMsg(error, 'PO belum Complete, selesaikan PO terlebih dahulu')
    }
  }
